<script setup>
/**
 * AgentClientDetailsView
 * Mobile-first client details page for agents
 */
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { doc, collection, query, where, getDocs } from 'firebase/firestore'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import { useAuthStore } from '../../../app/store/index.js'
import { ROUTES } from '../../../core/config/constants.js'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'
import { useBackButtonClose } from '@core/composables/useBackButtonClose.js'
import { useCloudFunctions } from '@core/composables/useCloudFunctions.js'
import { formatDate, formatDateTime, formatPrice } from '@core/utils/formatting.js'
import {
  getClientOrders,
  formatOrderNumber,
  getStatusDisplayText,
  getStatusColorClass,
} from '@core/services/orders.js'
import ClientOrderHistory from '@ui/components/orders/ClientOrderHistory.vue'

// ============================================
// Router & Auth
// ============================================
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// ============================================
// State
// ============================================
const client = ref(null)
const isLoading = ref(true)
const error = ref(null)
const notFound = ref(false)
const unauthorized = ref(false)

// Active section (segmented control)
const activeSection = ref('details') // 'details' | 'commercial' | 'visits'

// Order history overlay
const orderHistoryRef = ref(null)
const showingOrders = ref(false)

// Intercept back button to close order history overlay instead of navigating
useBackButtonClose(showingOrders, () => {
  if (orderHistoryRef.value) {
    orderHistoryRef.value.isActive = false
  }
  showingOrders.value = false
})

const { subscribe, unsubscribeAll } = useFirestoreSubscription()
const { callFunction } = useCloudFunctions()

// ============================================
// Financial Status
// ============================================
const financialLoading = ref(false)
const financialError = ref(false)
const financialData = ref(null) // { openDebt, openDocsCount, obligoRest, overdueDebt, daysToCollection, nextCollectionAmount, openDocs }
let financialFetchedMakat = null // track which makat was already fetched

// ============================================
// Linked App User
// ============================================
const linkedUsers = ref([]) // Array<{ uid, name, email, phone }>
let linkedUserFetchedMakat = null // track which makat was already fetched

// ============================================
// Client Orders (system pre-sale — "רוסמן" app-client orders)
// ============================================
const ordersLoading = ref(false)
const ordersError = ref(false)
const ordersData = ref([]) // pre_sale order docs, newest first
let ordersFetchedMakat = null // track which makat was already fetched

// Raw OBLIGO_FNCITEMS_SUBFORM rows → clean open-document objects for the modal
function mapOpenDocs(items) {
  if (!Array.isArray(items)) return []
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  return items.map((raw, i) => {
    const dueDate = raw.FNCDATE ? new Date(raw.FNCDATE) : null
    const isOverdue = Boolean(dueDate && dueDate < todayStart)
    return {
      id: raw.FNCTRANS ?? `${raw.FNCNUM || ''}-${i}`,
      docNumber: raw.IVNUM || raw.FNCNUM || '—',
      type: raw.FNCPATNAME || '',
      details: raw.DETAILS || '',
      docDate: raw.BALDATE || null,
      dueDate: raw.FNCDATE || null,
      amount: raw.SUM1 ?? 0,
      isOverdue,
      daysUntilDue:
        dueDate && !isOverdue
          ? Math.round((dueDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24))
          : null,
    }
  })
}

async function fetchFinancialStatus(makat) {
  if (!makat || financialFetchedMakat === makat) return
  financialFetchedMakat = makat
  financialLoading.value = true
  financialError.value = false
  financialData.value = null

  try {
    const obligo = await callFunction('priorityGetClientObligo', { clientMakat: String(makat) })

    if (!obligo?.ok) {
      financialError.value = true
      return
    }

    financialData.value = {
      openDebt: obligo.totalDebt ?? null,
      openDocsCount: Array.isArray(obligo.items) ? obligo.items.length : null,
      obligoRest: obligo.summary?.obligoRest ?? null,
      overdueDebt: obligo.overdueDebt ?? null,
      daysToCollection: obligo.daysUntilNextCollection ?? null,
      nextCollectionAmount: obligo.nextCollectionAmount ?? null,
      openDocs: mapOpenDocs(obligo.items),
    }
  } catch {
    financialError.value = true
  } finally {
    financialLoading.value = false
  }
}

// Find every app user linked to this client (primary or sub-client).
// linked_makats covers primary + sub-clients; client_makat is a legacy fallback
// for docs that predate linked_makats. A client may have several app users, so we
// run both queries and dedupe by uid to list them all.
async function fetchLinkedUser(makat) {
  if (!makat || linkedUserFetchedMakat === makat) return
  linkedUserFetchedMakat = makat
  linkedUsers.value = []

  try {
    const db = getFirebaseDb()
    const usersRef = collection(db, 'Users')
    const makatStr = String(makat)

    const [linkedSnap, legacySnap] = await Promise.all([
      getDocs(query(usersRef, where('linked_makats', 'array-contains', makatStr))),
      getDocs(query(usersRef, where('client_makat', '==', makatStr))),
    ])

    const byUid = new Map()
    for (const d of [...linkedSnap.docs, ...legacySnap.docs]) {
      if (byUid.has(d.id)) continue
      const data = d.data()
      byUid.set(d.id, {
        uid: d.id,
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
      })
    }

    linkedUsers.value = Array.from(byUid.values())
  } catch (err) {
    console.error('Linked user fetch error:', err)
    linkedUsers.value = []
  }
}

// Fetch the client's system orders — only pre_sale (the "רוסמן" app-client orders
// shown under the "רוסמן" tab in /app/admin/orders). Independent of the financial
// fetch so the spinner stays confined to the orders KPI tile.
async function fetchClientOrders(makat) {
  if (!makat || ordersFetchedMakat === makat) return
  ordersFetchedMakat = makat
  ordersLoading.value = true
  ordersError.value = false
  ordersData.value = []

  try {
    const all = await getClientOrders(String(makat))
    ordersData.value = all.filter((o) => o.pre_sale === true)
  } catch (err) {
    console.error('Client orders fetch error:', err)
    ordersError.value = true
  } finally {
    ordersLoading.value = false
  }
}

function retryClientOrders() {
  ordersFetchedMakat = null
  if (client.value?.makat) {
    fetchClientOrders(client.value.makat)
  }
}

function retryFinancial() {
  financialFetchedMakat = null
  if (client.value?.makat) {
    fetchFinancialStatus(client.value.makat)
  }
}

const hasOverdueDebt = computed(() => {
  return financialData.value?.overdueDebt != null && financialData.value.overdueDebt > 0
})

function formatFinancialCurrency(val) {
  if (val === null || val === undefined) return '—'
  return `₪${val.toLocaleString('he-IL', { maximumFractionDigits: 0 })}`
}

// ============================================
// Open Documents Modal (חוב פתוח / חוב בפיגור)
// ============================================
const showOpenDocs = ref(false)
const openDocsFilter = ref('all') // 'all' | 'overdue'

// Hardware/browser back closes the modal instead of leaving the client view
useBackButtonClose(showOpenDocs, () => {
  showOpenDocs.value = false
})

function openOpenDocsModal(filter) {
  const docs = financialData.value?.openDocs || []
  if (docs.length === 0) return
  if (filter === 'overdue' && !hasOverdueDebt.value) return
  openDocsFilter.value = filter
  showOpenDocs.value = true
}

// Overdue docs first, then upcoming by due date ascending (collection priority)
const modalDocs = computed(() => {
  const docs = financialData.value?.openDocs || []
  const shown = openDocsFilter.value === 'overdue' ? docs.filter((d) => d.isOverdue) : docs
  return [...shown].sort((a, b) => {
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1
    const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
    const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
    return aTime - bTime
  })
})

const modalTotals = computed(() => {
  let total = 0
  let overdue = 0
  for (const d of modalDocs.value) {
    total += d.amount
    if (d.isOverdue) overdue += d.amount
  }
  return { total, overdue, count: modalDocs.value.length }
})

function formatDocAmount(val) {
  if (val === null || val === undefined) return '—'
  const abs = Math.abs(val).toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${val < 0 ? '-' : ''}₪${abs}`
}

function collectionLabel(days) {
  if (days === 0) return 'גבייה היום'
  if (days === 1) return 'גבייה מחר'
  if (days === 2) return 'גבייה בעוד יומיים'
  return `גבייה בעוד ${days} ימים`
}

// ============================================
// Computed
// ============================================
const clientId = computed(() => route.params.clientId)

const isLinkedToAppUser = computed(() => linkedUsers.value.length > 0)

const linkedUserDisplayNames = computed(() =>
  linkedUsers.value.map((u) => u.name || u.email || u.phone || 'משתמש ללא שם')
)

const ordersCount = computed(() => ordersData.value.length)

// Orders list + read-only detail overlays (mirror the showOpenDocs pattern)
const showOrdersList = ref(false)
const showOrderDetail = ref(false)
const selectedOrder = ref(null)

// Hardware/browser back closes each overlay instead of leaving the client view.
// Detail sits above the list, so back closes detail first, then the list.
useBackButtonClose(showOrdersList, () => {
  showOrdersList.value = false
})
useBackButtonClose(showOrderDetail, () => {
  showOrderDetail.value = false
  selectedOrder.value = null
})

function openOrdersList() {
  if (ordersLoading.value || ordersError.value || ordersData.value.length === 0) return
  showOrdersList.value = true
}

function openOrderDetail(order) {
  selectedOrder.value = order
  showOrderDetail.value = true
}

function closeOrderDetail() {
  showOrderDetail.value = false
  selectedOrder.value = null
}

const { isAdmin } = storeToRefs(authStore)

const userAgentNumber = computed(() => {
  const agentValue = authStore.user?.profile?.agent
  if (agentValue === null || agentValue === undefined || agentValue === '') {
    return null
  }
  const num = Number(agentValue)
  return isNaN(num) ? null : num
})

// Check if navigating back should go to catalog
const isFromCatalog = computed(() => {
  return route.query.returnTo === 'catalog' && route.query.clientMakat
})

const backButtonText = computed(() => {
  return isFromCatalog.value ? 'חזרה לקטלוג' : 'חזרה לרשימה'
})

// ============================================
// Data Loading
// ============================================
function setupSubscription() {
  // Clear existing subscription
  unsubscribeAll()

  // Reset state
  client.value = null
  error.value = null
  notFound.value = false
  unauthorized.value = false
  isLoading.value = true

  if (!clientId.value) {
    notFound.value = true
    isLoading.value = false
    return
  }

  const db = getFirebaseDb()
  const docRef = doc(db, 'ClientsV2', clientId.value)

  subscribe(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        notFound.value = true
        client.value = null
      } else {
        const data = { id: snapshot.id, ...snapshot.data() }

        // Admin can view any client, agents can only view their assigned clients
        if (isAdmin.value) {
          // Admin bypass - authorized for any client
          client.value = data
          unauthorized.value = false
        } else {
          // Check agent authorization
          const clientAgent = Number(data.agent)
          if (userAgentNumber.value !== null && clientAgent !== userAgentNumber.value) {
            unauthorized.value = true
            client.value = null
          } else {
            client.value = data
            unauthorized.value = false
          }
        }
        notFound.value = false
      }
      isLoading.value = false
    },
    (err) => {
      console.error('Client subscription error:', err)
      error.value = err.message || 'שגיאה בטעינת לקוח'
      isLoading.value = false
    }
  )
}

// ============================================
// Lifecycle
// ============================================
onMounted(() => {
  setupSubscription()
})

// Watch for route param changes
watch(clientId, () => {
  financialFetchedMakat = null
  linkedUserFetchedMakat = null
  linkedUsers.value = []
  ordersFetchedMakat = null
  ordersData.value = []
  ordersError.value = false
  showOrdersList.value = false
  showOrderDetail.value = false
  selectedOrder.value = null
  setupSubscription()
})

// Fetch financial status + linked app user once client data is available
watch(client, (c) => {
  if (c?.makat) {
    fetchFinancialStatus(c.makat)
    fetchLinkedUser(c.makat)
  }
})

// Auto-load system orders in the background once we know the client is linked to an
// app user (the orders KPI only appears for linked clients). Spinner stays on the tile.
watch(isLinkedToAppUser, (linked) => {
  if (linked && client.value?.makat) {
    fetchClientOrders(client.value.makat)
  }
})

// ============================================
// Navigation
// ============================================
function goBack() {
  // Check if we should return to catalog
  const returnTo = route.query.returnTo
  const clientMakat = route.query.clientMakat

  if (returnTo === 'catalog' && clientMakat) {
    router.push({
      name: ROUTES.AGENTS_CATALOG,
      params: { clientMakat },
    })
  } else {
    router.push({ name: ROUTES.AGENTS_CLIENTS })
  }
}

// ============================================
// Quick Actions
// ============================================
function callClient() {
  if (client.value?.phone) {
    window.location.href = `tel:${client.value.phone}`
  }
}

function openMaps() {
  if (client.value?.address || client.value?.city) {
    const query = encodeURIComponent(
      [client.value.address, client.value.city].filter(Boolean).join(', ')
    )
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank')
  }
}

function openOrders() {
  if (client.value?.makat) {
    orderHistoryRef.value?.open({
      makat: client.value.makat,
      name: client.value.name || '',
    })
  }
}

function goToUpdateClient() {
  router.push({ name: ROUTES.AGENTS_UPDATE_CLIENT, query: { clientMakat: client.value?.makat } })
}

function goToAppTransfer() {
  router.push({ name: ROUTES.AGENTS_APP_TRANSFER, query: { clientMakat: client.value?.makat } })
}

function goToGvia() {
  router.push({ name: ROUTES.AGENTS_GVIA, query: { clientMakat: client.value?.makat } })
}

function goToPaymentTermsRequest() {
  const clientId = client.value?.makat || route.params.clientId
  if (!clientId) return
  router.push({
    name: ROUTES.AGENTS_PAYMENT_TERMS_REQUEST,
    params: { clientId: String(clientId) },
  })
}

function openAlbum() {
  if (!client.value?.makat) return
  router.push({
    name: ROUTES.AGENTS_CATALOG,
    params: { clientMakat: client.value.makat },
    query: { agentOverride: client.value.agent },
  })
}

// ============================================
// Helpers
// ============================================
function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—'
  return val
}

function formatBoolean(val) {
  if (val === true) return 'כן'
  if (val === false) return 'לא'
  return '—'
}

// Visitation parsing
const hebrewDays = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת']

function formatVisitation(visitation) {
  if (!visitation || typeof visitation !== 'object') return null

  const dayNum = Number(visitation.DAYNUM)
  if (isNaN(dayNum) || dayNum < 1 || dayNum > 7) return null

  const dayName = hebrewDays[dayNum - 1] || `יום ${dayNum}`

  const weeks = []
  if (visitation.WEEK1 === 'Y') weeks.push('1')
  if (visitation.WEEK2 === 'Y') weeks.push('2')
  if (visitation.WEEK3 === 'Y') weeks.push('3')
  if (visitation.WEEK4 === 'Y') weeks.push('4')

  return {
    dayName,
    dayNum,
    weeks,
    weeksText: weeks.length > 0 ? `שבועות: ${weeks.join(', ')}` : 'ללא שבועות מוגדרים',
  }
}
</script>

<template>
  <div class="client-details-view">
    <!-- Loading State -->
    <div v-if="isLoading" class="state-container">
      <div class="spinner"></div>
      <p class="state-text">טוען פרטי לקוח...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="state-container">
      <svg
        viewBox="0 0 24 24"
        width="48"
        height="48"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p class="state-text">{{ error }}</p>
      <button class="btn-back" @click="goBack">{{ backButtonText }}</button>
    </div>

    <!-- Not Found State -->
    <div v-else-if="notFound" class="state-container">
      <svg
        viewBox="0 0 24 24"
        width="48"
        height="48"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M16 16s-1.5-2-4-2-4 2-4 2" />
        <line x1="9" y1="9" x2="9.01" y2="9" />
        <line x1="15" y1="9" x2="15.01" y2="9" />
      </svg>
      <p class="state-text">לקוח לא נמצא</p>
      <button class="btn-back" @click="goBack">{{ backButtonText }}</button>
    </div>

    <!-- Unauthorized State -->
    <div v-else-if="unauthorized" class="state-container">
      <svg
        viewBox="0 0 24 24"
        width="48"
        height="48"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
      <p class="state-text">אין הרשאה לצפות בלקוח זה</p>
      <button class="btn-back" @click="goBack">{{ backButtonText }}</button>
    </div>

    <!-- Client Details -->
    <template v-else-if="client">
      <!-- Sticky Header -->
      <header class="details-header">
        <button class="back-btn" @click="goBack" aria-label="חזרה">
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        <div class="header-content">
          <h1 class="header-title">{{ formatValue(client.name) }}</h1>
          <p class="header-subtitle">
            <span class="header-makat">{{ formatValue(client.makat) }}</span>
            <span v-if="client.city" class="header-separator">•</span>
            <span v-if="client.city">{{ client.city }}</span>
          </p>
        </div>
      </header>

      <!-- Navigation Actions -->
      <div class="nav-actions">
        <button class="action-btn" @click="goToUpdateClient">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          <span>עדכון פרטי</span>
        </button>

        <button
          class="action-btn"
          :class="{ 'action-btn--linked': isLinkedToAppUser }"
          @click="goToAppTransfer"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
          <span>חיבור לאפליקצייה</span>
        </button>

        <button class="action-btn" @click="goToGvia">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          <span>גבייה</span>
        </button>

        <button class="action-btn" @click="goToPaymentTermsRequest">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="15" y2="17" />
          </svg>
          <span>תנאי תשלום</span>
        </button>
      </div>

      <!-- Financial Status Bar -->
      <div
        class="financial-bar"
        :class="{
          'financial-bar--loading': financialLoading,
          'financial-bar--success': financialData && !hasOverdueDebt,
          'financial-bar--danger': financialData && hasOverdueDebt,
          'financial-bar--error': financialError,
        }"
      >
        <!-- Loading -->
        <template v-if="financialLoading">
          <div class="financial-spinner"></div>
          <span class="financial-loading-text">בודק מצב כספי של הלקוח...</span>
        </template>

        <!-- Error -->
        <template v-else-if="financialError">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span class="financial-error-text">לא ניתן לטעון מצב כספי</span>
          <button class="financial-retry-btn" @click="retryFinancial">נסה שוב</button>
        </template>

        <!-- Data -->
        <template v-else-if="financialData">
          <div
            class="financial-chips"
            :class="{ 'financial-chips--with-orders': isLinkedToAppUser }"
          >
            <button
              class="financial-chip financial-chip--button"
              :disabled="!financialData.openDocs || financialData.openDocs.length === 0"
              @click="openOpenDocsModal('all')"
            >
              <span class="financial-chip-label">חוב פתוח</span>
              <span class="financial-chip-figure">
                <span class="financial-chip-value">{{
                  formatFinancialCurrency(financialData.openDebt)
                }}</span>
                <span v-if="financialData.openDocsCount" class="financial-chip-count"
                  >({{ financialData.openDocsCount }})</span
                >
              </span>
              <svg
                v-if="financialData.openDocs && financialData.openDocs.length > 0"
                class="financial-chip-chevron"
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <span class="financial-chip">
              <span class="financial-chip-label">י.אובליגו</span>
              <span class="financial-chip-figure">
                <span class="financial-chip-value">{{
                  formatFinancialCurrency(financialData.obligoRest)
                }}</span>
              </span>
            </span>
            <button
              class="financial-chip financial-chip--button"
              :class="{ 'financial-chip--danger': hasOverdueDebt }"
              :disabled="!hasOverdueDebt"
              @click="openOpenDocsModal('overdue')"
            >
              <span class="financial-chip-label">חוב בפיגור</span>
              <span class="financial-chip-figure">
                <span class="financial-chip-value">{{
                  formatFinancialCurrency(financialData.overdueDebt)
                }}</span>
              </span>
              <svg
                v-if="hasOverdueDebt"
                class="financial-chip-chevron"
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <span class="financial-chip">
              <span class="financial-chip-label">ימים לגבייה</span>
              <span class="financial-chip-figure">
                <span class="financial-chip-value">{{
                  financialData.daysToCollection != null ? financialData.daysToCollection : '—'
                }}</span>
              </span>
            </span>
            <!-- Orders KPI (system pre-sale orders) — only for app-linked clients -->
            <button
              v-if="isLinkedToAppUser"
              class="financial-chip financial-chip--button"
              :disabled="ordersLoading || (!ordersError && ordersCount === 0)"
              @click="ordersError ? retryClientOrders() : openOrdersList()"
            >
              <span class="financial-chip-label">הזמנות אפ.</span>
              <span class="financial-chip-figure">
                <span v-if="ordersLoading" class="orders-chip-spinner"></span>
                <span v-else-if="ordersError" class="orders-chip-retry">נסה שוב</span>
                <span v-else class="financial-chip-value">{{ ordersCount }}</span>
              </span>
              <svg
                v-if="!ordersLoading && !ordersError && ordersCount > 0"
                class="financial-chip-chevron"
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          </div>
        </template>
      </div>

      <!-- Segmented Control -->
      <div class="segment-control">
        <button
          class="segment-btn"
          :class="{ active: activeSection === 'details' }"
          @click="activeSection = 'details'"
        >
          פרטים
        </button>
        <button
          class="segment-btn"
          :class="{ active: activeSection === 'commercial' }"
          @click="activeSection = 'commercial'"
        >
          מסחרי
        </button>
        <button
          class="segment-btn"
          :class="{ active: activeSection === 'visits' }"
          @click="activeSection = 'visits'"
        >
          ביקורים
        </button>
      </div>

      <!-- Section Content (scrollable) -->
      <div class="section-content">
        <!-- Details Section -->
        <div v-if="activeSection === 'details'" class="section-panel">
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">מק״ט</span>
              <span class="info-value info-makat">{{ formatValue(client.makat) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">שם</span>
              <span class="info-value">{{ formatValue(client.name) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">ח.פ / ת.ז</span>
              <span class="info-value">{{ formatValue(client.vat) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">סטטוס</span>
              <span class="info-value">{{ formatValue(client.status) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">טלפון</span>
              <span class="info-value info-phone">{{ formatValue(client.phone) }}</span>
            </div>
            <div class="info-item info-item-full">
              <span class="info-label">כתובת</span>
              <span class="info-value">{{ formatValue(client.address) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">עיר</span>
              <span class="info-value">{{ formatValue(client.city) }}</span>
            </div>
          </div>
        </div>

        <!-- Commercial Section -->
        <div v-if="activeSection === 'commercial'" class="section-panel">
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">מחירון</span>
              <span class="info-value">{{ formatValue(client.price_list_id) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">צפייה במחירים</span>
              <span class="info-value">{{ formatBoolean(client.prices) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">תשלום</span>
              <span class="info-value">{{ formatValue(client.payment) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">סוג תשלום</span>
              <span class="info-value">{{ formatValue(client.payment_type) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">אובליגו</span>
              <span class="info-value">{{ formatValue(client.obligo) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">הזמנה מינימלית</span>
              <span class="info-value">{{ formatValue(client.min_order) }}</span>
            </div>
          </div>
        </div>

        <!-- Visits Section -->
        <div v-if="activeSection === 'visits'" class="section-panel">
          <template v-if="formatVisitation(client.visitation)">
            <div class="visitation-card">
              <div class="visitation-day">
                <span class="visitation-day-label">יום ביקור</span>
                <span class="visitation-day-value"
                  >יום {{ formatVisitation(client.visitation).dayName }}</span
                >
              </div>
              <div class="visitation-weeks">
                <span class="visitation-weeks-label">שבועות פעילים</span>
                <div class="weeks-grid">
                  <span
                    v-for="week in 4"
                    :key="week"
                    class="week-badge"
                    :class="{
                      active: formatVisitation(client.visitation).weeks.includes(String(week)),
                    }"
                  >
                    {{ week }}
                  </span>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="empty-section">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p>לא הוגדרו ביקורים ללקוח זה</p>
            </div>
          </template>
        </div>
      </div>

      <!-- Card Footer - Continue to Album -->
      <div class="card-footer">
        <div v-if="isLinkedToAppUser" class="linked-user-bar">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span class="linked-user-text">
            {{ linkedUsers.length > 1 ? 'לקוח משויך למשתמשים:' : 'לקוח משויך למשתמש:' }}
            <strong>{{ linkedUserDisplayNames.join(', ') }}</strong>
          </span>
        </div>
        <button class="btn-continue-album" :disabled="!client.makat" @click="openAlbum">
          המשך לאלבום לקוח
        </button>
      </div>

      <!-- Order History Overlay -->
      <div class="orders-overlay" :class="{ active: showingOrders }">
        <ClientOrderHistory
          ref="orderHistoryRef"
          @show="showingOrders = true"
          @close="showingOrders = false"
        />
      </div>

      <!-- Open Documents Modal -->
      <Transition name="docs-modal">
        <div v-if="showOpenDocs" class="docs-modal">
          <header class="docs-modal-header">
            <button class="back-btn" aria-label="חזרה לפרטי לקוח" @click="showOpenDocs = false">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
            <div class="header-content">
              <h2 class="header-title">
                {{ openDocsFilter === 'overdue' ? 'מסמכים בפיגור' : 'מסמכים פתוחים' }}
              </h2>
              <p class="header-subtitle">{{ formatValue(client.name) }}</p>
            </div>
          </header>

          <div class="docs-summary">
            <div class="docs-summary-tile">
              <span class="docs-summary-label">סה״כ</span>
              <span class="docs-summary-value">{{
                formatFinancialCurrency(modalTotals.total)
              }}</span>
            </div>
            <div
              class="docs-summary-tile"
              :class="{ 'docs-summary-tile--danger': modalTotals.overdue > 0 }"
            >
              <span class="docs-summary-label">מזה בפיגור</span>
              <span class="docs-summary-value">{{
                formatFinancialCurrency(modalTotals.overdue)
              }}</span>
            </div>
            <div class="docs-summary-tile">
              <span class="docs-summary-label">מסמכים</span>
              <span class="docs-summary-value">{{ modalTotals.count }}</span>
            </div>
          </div>

          <div class="docs-list">
            <div v-if="modalDocs.length === 0" class="empty-section">
              <p>אין מסמכים להצגה</p>
            </div>
            <div
              v-for="docItem in modalDocs"
              :key="docItem.id"
              class="doc-row"
              :class="{ 'doc-row--overdue': docItem.isOverdue }"
            >
              <div class="doc-row-top">
                <span class="doc-number">{{ docItem.docNumber }}</span>
                <span class="doc-amount" :class="{ 'doc-amount--credit': docItem.amount < 0 }">{{
                  formatDocAmount(docItem.amount)
                }}</span>
              </div>
              <div v-if="docItem.type || docItem.details" class="doc-type">
                {{ [docItem.type, docItem.details].filter(Boolean).join(' · ') }}
              </div>
              <div class="doc-dates">
                <span v-if="docItem.docDate">הופק: {{ formatDate(docItem.docDate) }}</span>
                <span v-if="docItem.dueDate">לפירעון: {{ formatDate(docItem.dueDate) }}</span>
                <span v-if="docItem.isOverdue" class="doc-overdue-badge">בפיגור</span>
              </div>
              <div
                v-if="!docItem.isOverdue && docItem.daysUntilDue != null && docItem.amount > 0"
                class="doc-collection"
              >
                {{ collectionLabel(docItem.daysUntilDue) }}
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Orders List Modal (system pre-sale orders) -->
      <Transition name="docs-modal">
        <div v-if="showOrdersList" class="docs-modal">
          <header class="docs-modal-header">
            <button class="back-btn" aria-label="חזרה לפרטי לקוח" @click="showOrdersList = false">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
            <div class="header-content">
              <h2 class="header-title">הזמנות</h2>
              <p class="header-subtitle">{{ formatValue(client.name) }}</p>
            </div>
          </header>

          <div class="docs-summary">
            <div class="docs-summary-tile">
              <span class="docs-summary-label">סה״כ הזמנות</span>
              <span class="docs-summary-value">{{ ordersCount }}</span>
            </div>
          </div>

          <div class="docs-list">
            <div v-if="ordersData.length === 0" class="empty-section">
              <p>אין הזמנות להצגה</p>
            </div>
            <button
              v-for="order in ordersData"
              :key="order.id"
              class="order-row"
              @click="openOrderDetail(order)"
            >
              <div class="order-row-top">
                <span class="order-row-number">{{ formatOrderNumber(order.orderNumber) }}</span>
                <span class="order-status-pill" :class="getStatusColorClass(order.status)">
                  {{ getStatusDisplayText(order.status) }}
                </span>
              </div>
              <div class="order-row-meta">
                <span>{{ formatDate(order.createdAt) }}</span>
                <span>{{ order.itemCount }} יח׳</span>
                <span class="order-row-total">{{ formatPrice(order.totals?.totalWithVat) }} ₪</span>
              </div>
            </button>
          </div>
        </div>
      </Transition>

      <!-- Order Detail Modal (read-only) -->
      <Transition name="docs-modal">
        <div v-if="showOrderDetail && selectedOrder" class="docs-modal">
          <header class="docs-modal-header">
            <button class="back-btn" aria-label="חזרה לרשימת הזמנות" @click="closeOrderDetail">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
            <div class="header-content">
              <h2 class="header-title">הזמנה {{ formatOrderNumber(selectedOrder.orderNumber) }}</h2>
              <p class="header-subtitle">{{ formatValue(client.name) }}</p>
            </div>
          </header>

          <div class="order-detail-scroll">
            <div class="order-detail-meta">
              <span class="order-status-pill" :class="getStatusColorClass(selectedOrder.status)">
                {{ getStatusDisplayText(selectedOrder.status) }}
              </span>
              <span class="order-detail-date">{{ formatDateTime(selectedOrder.createdAt) }}</span>
            </div>

            <div class="order-detail-items">
              <div v-for="(item, i) in selectedOrder.items" :key="i" class="order-item-card">
                <div class="order-item-top">
                  <span class="order-item-name">{{ item.name }}</span>
                  <span class="order-item-total">{{ formatPrice(item.lineTotalPreVat) }} ₪</span>
                </div>
                <div class="order-item-meta">
                  <span class="order-item-makat">{{ item.makat }}</span>
                  <span
                    >{{ item.qty }} יח׳ × {{ formatPrice(item.unitPricePreVatPreDeposit) }} ₪</span
                  >
                  <span v-if="item.promo" class="order-item-badge">{{ item.promo.label }}</span>
                  <span v-if="item.discount" class="order-item-badge"
                    >הנחה {{ item.discount.discountPercent }}%</span
                  >
                </div>
              </div>
            </div>

            <div class="order-detail-totals">
              <div class="order-total-row">
                <span>סה״כ פריטים (לפני מע״מ)</span>
                <span>{{ formatPrice(selectedOrder.totals?.itemsSubtotalPreVat) }} ₪</span>
              </div>
              <div v-if="selectedOrder.deposit?.hasDeposit" class="order-total-row">
                <span>פיקדון ({{ selectedOrder.deposit.depositUnits }} יח׳)</span>
                <span>{{ formatPrice(selectedOrder.deposit.depositPreVat) }} ₪</span>
              </div>
              <div
                v-if="selectedOrder.totals?.promoSavingsPreVat > 0"
                class="order-total-row order-total-savings"
              >
                <span>חיסכון מבצעים</span>
                <span>-{{ formatPrice(selectedOrder.totals.promoSavingsPreVat) }} ₪</span>
              </div>
              <div
                v-if="selectedOrder.totals?.discountSavingsPreVat > 0"
                class="order-total-row order-total-savings"
              >
                <span>חיסכון הנחות</span>
                <span>-{{ formatPrice(selectedOrder.totals.discountSavingsPreVat) }} ₪</span>
              </div>
              <div class="order-total-row">
                <span>סה״כ לפני מע״מ</span>
                <span>{{ formatPrice(selectedOrder.totals?.totalPreVat) }} ₪</span>
              </div>
              <div class="order-total-row">
                <span>מע״מ</span>
                <span>{{ formatPrice(selectedOrder.totals?.vatAmount) }} ₪</span>
              </div>
              <div class="order-total-row order-total-grand">
                <span>סה״כ</span>
                <span>{{ formatPrice(selectedOrder.totals?.totalWithVat) }} ₪</span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </template>
  </div>
</template>

<style scoped>
/* ============================================
   Layout
   ============================================ */
.client-details-view {
  height: calc(100vh - var(--header-height));
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ============================================
   State Containers
   ============================================ */
.state-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl);
  text-align: center;
  color: var(--text-muted);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.state-text {
  margin: var(--spacing-md) 0;
  font-size: var(--font-size-base);
  color: var(--text-secondary);
}

.btn-back {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.btn-back:hover {
  background: var(--color-primary-dark);
}

/* ============================================
   Sticky Header
   ============================================ */
.details-header {
  flex-shrink: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.back-btn:hover {
  background: var(--bg-secondary);
  color: var(--color-primary);
}

.header-content {
  flex: 1;
  min-width: 0;
}

.header-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 2px 0 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.header-makat {
  font-family: monospace;
}

.header-separator {
  opacity: 0.5;
}

.action-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.action-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
  background: rgba(40, 53, 72, 0.05);
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Green state when the client already has a linked app user */
.action-btn--linked,
.action-btn--linked:hover:not(:disabled) {
  background: var(--color-success);
  border-color: var(--color-success);
  color: #fff;
}

.action-btn--linked:hover:not(:disabled) {
  filter: brightness(0.96);
}

/* ============================================
   Navigation Actions
   ============================================ */
.nav-actions {
  flex-shrink: 0;
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

/* ============================================
   Orders Overlay
   ============================================ */
.orders-overlay.active {
  display: flex;
  flex-direction: column;
  position: fixed;
  inset: 0;
  top: var(--header-height, 0);
  z-index: 100;
  background: var(--bg-secondary);
}

/* ============================================
   Financial Status Bar
   ============================================ */
.financial-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  min-height: 44px;
  border-bottom: 1px solid var(--border-color);
  transition: background var(--transition-fast);
}

.financial-bar--loading {
  background: var(--bg-secondary);
  color: var(--text-muted);
}

.financial-bar--success {
  background: rgba(16, 185, 129, 0.08);
  border-bottom-color: rgba(16, 185, 129, 0.2);
}

.financial-bar--danger {
  background: rgba(239, 68, 68, 0.08);
  border-bottom-color: rgba(239, 68, 68, 0.2);
}

.financial-bar--error {
  background: var(--bg-secondary);
  color: var(--text-muted);
}

.financial-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

.financial-loading-text {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.financial-error-text {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.financial-retry-btn {
  padding: 2px var(--spacing-sm);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--color-primary);
  font-size: var(--font-size-xs);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.financial-retry-btn:hover {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);
}

.financial-chips {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-sm);
  width: 100%;
}

.financial-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 62px;
  height: 100%;
  margin: 0;
  padding: var(--spacing-sm);
  box-sizing: border-box;
  text-align: center;
  border-radius: var(--border-radius);
  font-size: var(--font-size-xs);
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.financial-bar--danger .financial-chip {
  border-color: rgba(239, 68, 68, 0.2);
}

.financial-chip--danger {
  background: rgba(239, 68, 68, 0.1) !important;
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.financial-chip-label {
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.financial-chip-figure {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  direction: ltr;
  unicode-bidi: embed;
}

.financial-chip-value {
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
}

.financial-chip-count {
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
}

.financial-bar--danger .financial-chip-value {
  color: var(--color-error);
}

/* Tablet and up: 4-across KPI tiles with larger figures */
@media (min-width: 768px) {
  .financial-bar {
    padding: var(--spacing-md);
  }

  .financial-chips {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    max-width: 760px;
    margin: 0 auto;
  }

  .financial-chip {
    min-height: 72px;
    padding: var(--spacing-sm) var(--spacing-md);
  }

  .financial-chip-label {
    font-size: var(--font-size-sm);
  }

  .financial-chip-value {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
  }

  .financial-chip-count {
    font-size: var(--font-size-sm);
  }
}

.financial-bar--success .financial-chip-value {
  color: var(--color-success);
}

.financial-chip--danger .financial-chip-value {
  color: var(--color-error) !important;
}

/* Clickable chips (חוב פתוח / חוב בפיגור) */
.financial-chip--button {
  position: relative;
  font-family: var(--font-family);
  font-size: var(--font-size-xs);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.financial-chip--button:not(:disabled):hover {
  border-color: var(--color-primary);
}

.financial-chip--button:not(:disabled):active {
  transform: scale(0.97);
}

.financial-chip--button:disabled {
  cursor: default;
}

.financial-chip-chevron {
  position: absolute;
  inset-inline-start: 8px;
  top: 50%;
  transform: translateY(-50%);
  flex-shrink: 0;
  color: var(--text-muted);
}

/* ============================================
   Open Documents Modal
   ============================================ */
.docs-modal {
  position: fixed;
  inset: 0;
  top: var(--header-height, 0);
  z-index: 110;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
}

.docs-modal-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.docs-summary {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.docs-summary-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: var(--spacing-sm);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  text-align: center;
}

.docs-summary-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
}

.docs-summary-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  direction: ltr;
  unicode-bidi: embed;
}

.docs-summary-tile--danger .docs-summary-value {
  color: var(--color-error);
}

.docs-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
}

.doc-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
}

.doc-row--overdue {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.25);
  border-inline-start: 3px solid var(--color-error);
}

.doc-row-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.doc-number {
  font-family: monospace;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  direction: ltr;
  unicode-bidi: embed;
}

.doc-amount {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  direction: ltr;
  unicode-bidi: embed;
  white-space: nowrap;
}

.doc-row--overdue .doc-amount {
  color: var(--color-error);
}

.doc-amount--credit {
  color: var(--color-success);
}

.doc-type {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.doc-dates {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.doc-overdue-badge {
  padding: 1px 8px;
  background: rgba(239, 68, 68, 0.15);
  border-radius: 999px;
  color: var(--color-error);
  font-size: 11px;
  font-weight: var(--font-weight-medium);
}

.doc-collection {
  margin-top: 2px;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-info);
}

/* Desktop: constrain content width for readability */
@media (min-width: 768px) {
  .docs-summary,
  .docs-list {
    padding-inline: max(var(--spacing-md), calc((100% - 760px) / 2));
  }
}

/* Enter/leave transition */
.docs-modal-enter-active,
.docs-modal-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.docs-modal-enter-from,
.docs-modal-leave-to {
  transform: translateY(24px);
  opacity: 0;
}

/* ============================================
   Segmented Control
   ============================================ */
.segment-control {
  flex-shrink: 0;
  display: flex;
  gap: 4px;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
}

.segment-btn {
  flex: 1;
  padding: var(--spacing-sm);
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.segment-btn:hover {
  background: var(--bg-secondary);
}

.segment-btn.active {
  background: var(--color-primary);
  color: white;
}

/* ============================================
   Section Content
   ============================================ */
.section-content {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-md);
}

/* ============================================
   Card Footer
   ============================================ */
.card-footer {
  flex-shrink: 0;
  padding: 6px var(--spacing-sm);
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
}

.btn-continue-album {
  display: block;
  width: 100%;
  padding: 12px var(--spacing-md);
  background: var(--color-primary);
  color: #fff;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.btn-continue-album:hover {
  background: var(--color-primary-hover);
}

.btn-continue-album:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Linked-user bar (above the album button) */
.linked-user-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  margin-bottom: 6px;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: var(--border-radius);
  color: var(--color-success);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.linked-user-text strong {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.section-panel {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item-full {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: uppercase;
}

.info-value {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  word-break: break-word;
}

.info-makat {
  font-family: monospace;
  color: var(--color-primary);
}

.info-phone {
  direction: ltr;
  text-align: right;
}

/* Visitation */
.visitation-card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.visitation-day,
.visitation-weeks {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.visitation-day-label,
.visitation-weeks-label {
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: uppercase;
}

.visitation-day-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.weeks-grid {
  display: flex;
  gap: var(--spacing-sm);
}

.week-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
}

.week-badge.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

/* Empty Section */
.empty-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  color: var(--text-muted);
  text-align: center;
}

.empty-section p {
  margin: var(--spacing-sm) 0 0;
  font-size: var(--font-size-sm);
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .header-title {
    font-size: var(--font-size-base);
  }
}

@media (min-width: 769px) {
  .client-details-view {
    padding: var(--spacing-lg);
  }

  .details-header {
    border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
  }

  .nav-actions {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
  }

  .financial-bar {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
  }

  .financial-bar--success {
    border-left-color: rgba(16, 185, 129, 0.2);
    border-right-color: rgba(16, 185, 129, 0.2);
  }

  .financial-bar--danger {
    border-left-color: rgba(239, 68, 68, 0.2);
    border-right-color: rgba(239, 68, 68, 0.2);
  }

  .segment-control {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
    border-left: 1px solid var(--border-color);
    border-right: 1px solid var(--border-color);
  }

  .section-content {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
    padding: var(--spacing-md);
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    border-top: none;
  }

  .section-panel {
    border: none;
    padding: 0;
  }

  .card-footer {
    margin: 0 auto;
    max-width: 800px;
    width: 100%;
    border: 1px solid var(--border-color);
    border-top: none;
    border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  }
}

/* ============================================
   Orders KPI chip (system pre-sale orders, inside .financial-chips)
   ============================================ */
.orders-chip-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.orders-chip-retry {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  text-decoration: underline;
}

/* Tablet and up: linked clients get a 5th tile — keep all KPIs in one row */
@media (min-width: 768px) {
  .financial-chips--with-orders {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

/* ============================================
   Orders List rows (inside .docs-modal / .docs-list)
   ============================================ */
.order-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  text-align: start;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.order-row:hover {
  border-color: var(--color-primary);
}

.order-row:active {
  transform: scale(0.99);
}

.order-row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.order-row-number {
  font-family: monospace;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  direction: ltr;
  unicode-bidi: embed;
}

.order-row-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.order-row-total {
  margin-inline-start: auto;
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  direction: ltr;
  unicode-bidi: embed;
}

/* Status pill (getStatusColorClass output) — these classes are only defined here */
.order-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  white-space: nowrap;
}
.order-status-pill.status-new,
.order-status-pill.status-paid,
.order-status-pill.status-delivered {
  background: rgba(16, 185, 129, 0.12);
  color: var(--color-success);
}
.order-status-pill.status-priority,
.order-status-pill.status-shipped {
  background: rgba(40, 53, 72, 0.1);
  color: var(--color-primary);
}
.order-status-pill.status-pending,
.order-status-pill.status-processing {
  background: rgba(245, 158, 11, 0.15);
  color: var(--color-warning);
}
.order-status-pill.status-cancelled {
  background: rgba(239, 68, 68, 0.12);
  color: var(--color-error);
}

/* ============================================
   Order Detail overlay (read-only)
   ============================================ */
.order-detail-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
}

.order-detail-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.order-detail-date {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.order-detail-items {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.order-item-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
}

.order-item-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-sm);
}

.order-item-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.order-item-total {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  direction: ltr;
  unicode-bidi: embed;
  white-space: nowrap;
}

.order-item-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.order-item-makat {
  font-family: monospace;
  color: var(--color-primary);
}

.order-item-badge {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(40, 53, 72, 0.1);
  color: var(--color-primary);
  font-size: 11px;
}

.order-detail-totals {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: var(--spacing-md);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
}

.order-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.order-total-row span:last-child {
  direction: ltr;
  unicode-bidi: embed;
  color: var(--text-primary);
}

.order-total-savings,
.order-total-savings span:last-child {
  color: var(--color-success);
}

.order-total-grand {
  padding-top: var(--spacing-sm);
  margin-top: var(--spacing-xs);
  border-top: 2px solid var(--border-color);
  font-weight: var(--font-weight-bold);
}

.order-total-grand span {
  font-size: var(--font-size-lg);
  color: var(--color-primary);
}

/* Desktop: constrain overlay content width (mirror .docs-summary / .docs-list rule) */
@media (min-width: 768px) {
  .order-detail-scroll {
    padding-inline: max(var(--spacing-md), calc((100% - 760px) / 2));
  }
}
</style>
